import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import {
  createCountingPlan,
  getInventoryRecommendations,
  downloadPlanExcel,
} from '../../../../api/inventoryCountingApi';

export default function RecommendationTable({ refreshKey, onPlanCreated }) {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState({});
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [createdPlanId, setCreatedPlanId] = useState(null);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);

  const loadRecommendations = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getInventoryRecommendations();
      setRows(result.rows || []);
      setSelected({});
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Nie udało się pobrać rekomendacji.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecommendations();
  }, [refreshKey]);

  const selectedItems = useMemo(() => {
    return rows
      .filter((row) => selected[row.product])
      .flatMap((row) =>
        row.bins.map((bin) => ({
          bin,
          product: row.product,
          description: row.description,
        })),
      );
  }, [rows, selected]);

  //   const toggleRow = (product) => {
  //     setSelected((prev) => ({
  //       ...prev,
  //       [product]: !prev[product],
  //     }));
  //   };

  const toggleRow = (event, product, currentIndex) => {
    const checked = event.target.checked;

    // SHIFT + klik
    if (
      event.nativeEvent.shiftKey &&
      lastSelectedIndex !== null &&
      lastSelectedIndex !== currentIndex
    ) {
      const start = Math.min(lastSelectedIndex, currentIndex);
      const end = Math.max(lastSelectedIndex, currentIndex);

      setSelected((prev) => {
        const next = { ...prev };

        for (let i = start; i <= end; i++) {
          next[rows[i].product] = checked;
        }

        return next;
      });
    } else {
      setSelected((prev) => ({
        ...prev,
        [product]: checked,
      }));
    }

    setLastSelectedIndex(currentIndex);
  };

  const selectPriorityOne = () => {
    const next = {};
    rows.forEach((row) => {
      if (row.priority === 1) next[row.product] = true;
    });
    setSelected(next);
  };

  const clearSelection = () => {
    setSelected({});
  };

  const handleCreatePlan = async () => {
    if (!selectedItems.length) {
      setError('Nie zaznaczono żadnych pozycji.');
      return;
    }

    setSaving(true);
    setError(null);
    setMessage(null);
    setCreatedPlanId(null);

    try {
      const result = await createCountingPlan({
        items: selectedItems,
        comment,
      });

      setMessage(`Plan zapisany. Pozycji: ${result.itemsCount}`);
      setCreatedPlanId(result.planBatchId);
      setSelected({});
      setComment('');

      if (onPlanCreated) onPlanCreated();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Nie udało się zapisać planu.',
      );
    } finally {
      setSaving(false);
    }
  };

  const priorityColor = (priority) => {
    if (priority === 1) return 'success';
    if (priority === 2) return 'warning';
    return 'default';
  };
  const selectedProductsCount = Object.values(selected).filter(Boolean).length;
  const selectedBinsCount = selectedItems.length;
  const handleDownloadExcel = async () => {
    if (!createdPlanId) return;

    try {
      await downloadPlanExcel(createdPlanId);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Nie udało się pobrać pliku Excel.',
      );
    }
  };

  const selectAllVisible = () => {
    const next = {};
    rows.forEach((row) => {
      next[row.product] = true;
    });
    setSelected(next);
  };

  const selectPriorityTwo = () => {
    const next = {};
    rows.forEach((row) => {
      if (row.priority === 2) next[row.product] = true;
    });
    setSelected(next);
  };

  const selectWithDispatch = () => {
    const next = {};
    rows.forEach((row) => {
      if (row.hasDispatch) next[row.product] = true;
    });
    setSelected(next);
  };

  const selectWithoutDispatch = () => {
    const next = {};
    rows.forEach((row) => {
      if (!row.hasDispatch) next[row.product] = true;
    });
    setSelected(next);
  };

  return (
    <Card variant="outlined" sx={{ mt: 3 }}>
      <CardContent>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6">Rekomendacje do liczenia</Typography>
            <Typography variant="body2" color="text.secondary">
              Zaznacz produkty, które chcesz przekazać do liczenia.
            </Typography>
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            flexWrap="wrap"
          >
            <Button variant="outlined" onClick={selectAllVisible}>
              Zaznacz wszystko
            </Button>

            <Button variant="outlined" onClick={selectPriorityOne}>
              Priorytet 1
            </Button>

            <Button variant="outlined" onClick={selectPriorityTwo}>
              Priorytet 2
            </Button>

            <Button variant="outlined" onClick={selectWithoutDispatch}>
              Bez wywozów
            </Button>

            <Button variant="outlined" onClick={selectWithDispatch}>
              Z wywozami
            </Button>

            <Button variant="outlined" color="inherit" onClick={clearSelection}>
              Wyczyść
            </Button>

            <Button
              variant="contained"
              disabled={saving || !selectedItems.length}
              onClick={handleCreatePlan}
            >
              {saving ? 'Zapisywanie...' : 'Przekaż do liczenia'}
            </Button>
          </Stack>
        </Stack>

        <TextField
          fullWidth
          size="small"
          label="Komentarz do planu"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          sx={{ mb: 2 }}
        />

        {message && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            action={
              createdPlanId ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleDownloadExcel}
                >
                  Pobierz Excel
                </Button>
              ) : null
            }
          >
            {message}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ mb: 1 }}
        >
          <Typography variant="body2">
            Zaznaczone produkty: <strong>{selectedProductsCount}</strong>
          </Typography>

          <Typography variant="body2">
            Zaznaczone biny: <strong>{selectedBinsCount}</strong>
          </Typography>
        </Stack>

        {loading ? (
          <CircularProgress />
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Priorytet</TableCell>
                  <TableCell>Produkt</TableCell>
                  <TableCell>Opis</TableCell>
                  <TableCell>Biny</TableCell>
                  <TableCell>Wywóz</TableCell>
                  <TableCell>Liczba zliczeń</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={row.product} hover>
                    <TableCell padding="checkbox">
                      {/* <Checkbox
                        checked={Boolean(selected[row.product])}
                        onChange={() => toggleRow(row.product)}
                      /> */}
                      <Checkbox
                        checked={Boolean(selected[row.product])}
                        onChange={(event) =>
                          toggleRow(event, row.product, index)
                        }
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        color={priorityColor(row.priority)}
                        label={row.priorityDescription}
                      />
                    </TableCell>

                    <TableCell>{row.product}</TableCell>
                    <TableCell>{row.description || '-'}</TableCell>
                    <TableCell>{row.bins?.join(', ')}</TableCell>
                    <TableCell>
                      {row.hasDispatch ? (
                        <Chip size="small" color="warning" label="TAK" />
                      ) : (
                        <Chip size="small" color="success" label="NIE" />
                      )}
                    </TableCell>
                    <TableCell>{row.fullCountingCount}</TableCell>
                  </TableRow>
                ))}

                {!rows.length && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Brak rekomendacji
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}
